import apiService from './api';
import { unwrap } from '../utils/apiResponse';
import type { ApiError } from '../types/api';
import type {
  AckSubjectType,
  CertificationTypeOption,
  ContentVersion,
  EquipmentInductionDetail,
  GapCheck,
  InductionAccess,
  InductionBasis,
  InductionGap,
  InductionInput,
  InductionList,
  InductionPerson,
  InductionRegister,
  InductionRequest,
  MyInductions,
  Operators,
  PersonDetail,
  PersonRef,
  PolicyForReading,
  MyPolicyFacts,
  PolicySection,
  PolicyTemplate,
} from '../types/induction';

// ============================================================================
// INDUCTION SERVICE — /api/inductions
// Equipment inductions (who may operate what), the business induction pack
// (policies everyone acknowledges), sign-off requests, and the register.
// The gates live server-side on job create/assign/start and pre-start; this
// service only reads them and answers them.
// ============================================================================

class InductionService {
  async access(): Promise<InductionAccess> {
    return unwrap(await apiService.get<InductionAccess>('/inductions/access'));
  }

  async setEnforcement(enforce: boolean): Promise<{ enforce_equipment: boolean }> {
    return unwrap(await apiService.put<{ enforce_equipment: boolean }>('/inductions/settings', { enforce_equipment: enforce }));
  }

  // ---- me ------------------------------------------------------------------

  async mine(): Promise<MyInductions> {
    return unwrap(await apiService.get<MyInductions>('/inductions/me'));
  }

  async readPolicy(id: number): Promise<PolicyForReading> {
    return unwrap(await apiService.get<PolicyForReading>(`/inductions/me/policies/${id}`));
  }

  async acknowledge(subject_type: AckSubjectType, subject_id: number, version: number, signed_name?: string): Promise<void> {
    unwrap(await apiService.post('/inductions/me/acknowledge', { subject_type, subject_id, version, signed_name }));
  }

  async acknowledgeWitnessed(ref: PersonRef, subject_type: AckSubjectType, subject_id: number, version: number, signed_name: string): Promise<void> {
    unwrap(await apiService.post('/inductions/acknowledge/witnessed', { ref, subject_type, subject_id, version, signed_name }));
  }

  /** Ask a supervisor to sign me off on these machines (from the pre-start screen). */
  async requestForMe(equipmentIds: number[], jobId?: number | null): Promise<{ requested: number }> {
    return unwrap(await apiService.post<{ requested: number }>('/inductions/me/request', { equipment_ids: equipmentIds, job_id: jobId ?? null }));
  }

  async contentVersion(id: number): Promise<ContentVersion> {
    return unwrap(await apiService.get<ContentVersion>(`/inductions/versions/${id}`));
  }

  // ---- picker checks -------------------------------------------------------

  /** Which of these people are not inducted on which machines (all active machines if none given). */
  async check(userIds: number[], equipmentIds: number[] = []): Promise<GapCheck> {
    const qs = new URLSearchParams();
    if (userIds.length) qs.set('user_ids', userIds.join(','));
    if (equipmentIds.length) qs.set('equipment_ids', equipmentIds.join(','));
    return unwrap(await apiService.get<GapCheck>(`/inductions/check?${qs.toString()}`));
  }

  async operators(equipmentId: number): Promise<Operators> {
    return unwrap(await apiService.get<Operators>(`/inductions/equipment/${equipmentId}/operators`));
  }

  // ---- inductions ----------------------------------------------------------

  async list(): Promise<InductionList> {
    return unwrap(await apiService.get<InductionList>('/inductions/equipment'));
  }

  async get(id: number): Promise<EquipmentInductionDetail> {
    return unwrap(await apiService.get<EquipmentInductionDetail>(`/inductions/equipment/${id}`));
  }

  async create(input: { type_key?: string; equipment_id?: number }): Promise<EquipmentInductionDetail> {
    return unwrap(await apiService.post<EquipmentInductionDetail>('/inductions/equipment', input));
  }

  async update(id: number, input: InductionInput): Promise<EquipmentInductionDetail> {
    return unwrap(await apiService.put<EquipmentInductionDetail>(`/inductions/equipment/${id}`, input));
  }

  async certificationTypes(): Promise<CertificationTypeOption[]> {
    return unwrap(await apiService.get<CertificationTypeOption[]>('/inductions/certification-types')) ?? [];
  }

  // ---- sign-offs -----------------------------------------------------------

  async signOff(input: { ref: PersonRef; induction_ids: number[]; basis?: InductionBasis; notes?: string; expires_on?: string | null }): Promise<void> {
    unwrap(await apiService.post('/inductions/records', input));
  }

  async signOffBulk(entries: { ref: PersonRef; induction_ids: number[] }[], notes?: string, basis: 'assessed' | 'prior_competency' = 'prior_competency'): Promise<{ records: number; people: number }> {
    return unwrap(await apiService.post<{ records: number; people: number }>('/inductions/records/bulk', { entries, notes, basis }));
  }

  async revoke(recordId: number, reason: string): Promise<void> {
    unwrap(await apiService.post(`/inductions/records/${recordId}/revoke`, { reason }));
  }

  // ---- register ------------------------------------------------------------

  async register(): Promise<InductionRegister> {
    return unwrap(await apiService.get<InductionRegister>('/inductions/register'));
  }

  async people(): Promise<InductionPerson[]> {
    return unwrap(await apiService.get<InductionPerson[]>('/inductions/people')) ?? [];
  }

  async person(ref: PersonRef): Promise<PersonDetail> {
    return unwrap(await apiService.get<PersonDetail>(`/inductions/people/${ref}`));
  }

  // ---- requests ------------------------------------------------------------

  async requests(status: 'pending' | 'approved' | 'declined' | 'all' = 'pending'): Promise<InductionRequest[]> {
    return unwrap(await apiService.get<InductionRequest[]>(`/inductions/requests?status=${status}`)) ?? [];
  }

  async approve(id: number, note?: string): Promise<{ assigned: boolean; assign_error: string | null }> {
    return unwrap(await apiService.post<{ assigned: boolean; assign_error: string | null }>(`/inductions/requests/${id}/approve`, { note }));
  }

  async decline(id: number, note?: string): Promise<void> {
    unwrap(await apiService.post(`/inductions/requests/${id}/decline`, { note }));
  }

  // ---- pack ----------------------------------------------------------------

  // 'whs' starters belong to the WHS pack; 'employment' ones to Company Policies.
  async policyTemplates(section: PolicySection = 'whs'): Promise<PolicyTemplate[]> {
    return unwrap(await apiService.get<PolicyTemplate[]>(`/inductions/policy-templates?section=${section}`)) ?? [];
  }

  async installPolicyTemplates(keys: string[], section: PolicySection = 'whs'): Promise<{ id: number; title: string }[]> {
    return unwrap(await apiService.post<{ id: number; title: string }[]>('/inductions/policy-templates/install', { keys, section }));
  }

  /** A draft's text with its {{markers}} filled from the org's current settings. */
  async previewPolicy(content: string): Promise<{ content: string; markers: string[] }> {
    return unwrap(await apiService.post<{ content: string; markers: string[] }>('/inductions/policy-preview', { content }));
  }

  /** The figures the employment policy states, and that policy in my pack. */
  async myPolicyFacts(): Promise<MyPolicyFacts> {
    return unwrap(await apiService.get<MyPolicyFacts>('/inductions/me/policy-facts'));
  }
}

const inductionService = new InductionService();
export default inductionService;

/**
 * The gaps on a 409 NOT_INDUCTED / CERTIFICATE_REQUIRED refusal, or null if
 * the error is something else. ApiError keeps the whole `error` body as
 * `details`, and the server's own `details` sits inside it.
 */
export function inductionRefusal(err: unknown): { code: string; message: string; gaps: InductionGap[]; canOverride: boolean } | null {
  const e = err as ApiError | undefined;
  if (!e || (e.code !== 'NOT_INDUCTED' && e.code !== 'CERTIFICATE_REQUIRED')) return null;
  const body = (e.details ?? {}) as { details?: { gaps?: InductionGap[]; can_override?: boolean } };
  return {
    code: e.code,
    message: e.message,
    gaps: body.details?.gaps ?? [],
    canOverride: !!body.details?.can_override,
  };
}
